// The pomodoro: work, a short break, and a long break every N rounds. One
// timer for the whole app on this device, kept in localStorage so a reload
// or a switch between the app and the desk does not lose it. The countdown
// runs against a deadline in wall-clock time rather than a decremented
// counter, so a throttled tab or a laptop lid does not make it drift; when
// the phase is over, whichever screen notices first ends it and records the
// session in the library (metric `focused`), where Insights reads it.
import { create } from 'zustand'
import { api } from '@/runtime'

export type Phase = 'work' | 'short' | 'long'

export interface PomodoroConfig {
  work: number // minutes
  short: number
  long: number
  rounds: number // work phases before a long break
  autoNext: boolean
  sound: boolean
  notify: boolean
}

export interface PomodoroState {
  config: PomodoroConfig
  phase: Phase
  /** running: the phase ends at `deadline` (ms); paused: `remaining` ms are left; idle: nothing is going */
  status: 'idle' | 'running' | 'paused'
  deadline: number
  remaining: number
  /** which work phase of the set this is, 1-based */
  round: number
  startedAt: number | null
  taskId: string | null
  title: string | null
  /** the last phase that ended, so a screen can say so once */
  lastEnded: { phase: Phase; at: number } | null
}

export const PHASE_LABEL: Record<Phase, string> = { work: 'Focus', short: 'Short break', long: 'Long break' }

const KEY = 'pomodoro'
const DEFAULT_CONFIG: PomodoroConfig = {
  work: 25,
  short: 5,
  long: 15,
  rounds: 4,
  autoNext: true,
  sound: true,
  notify: true,
}

const read = (): Omit<PomodoroState, 'config'> & { config: PomodoroConfig } => {
  const base = {
    config: DEFAULT_CONFIG,
    phase: 'work' as Phase,
    status: 'idle' as const,
    deadline: 0,
    remaining: DEFAULT_CONFIG.work * 60_000,
    round: 1,
    startedAt: null,
    taskId: null,
    title: null,
    lastEnded: null,
  }
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return base
    const j = JSON.parse(raw) as Partial<PomodoroState>
    const config = { ...DEFAULT_CONFIG, ...(j.config ?? {}) }
    return {
      config,
      phase: j.phase === 'short' || j.phase === 'long' ? j.phase : 'work',
      status: j.status === 'running' || j.status === 'paused' ? j.status : 'idle',
      deadline: typeof j.deadline === 'number' ? j.deadline : 0,
      remaining: typeof j.remaining === 'number' ? j.remaining : config.work * 60_000,
      round: typeof j.round === 'number' && j.round >= 1 ? j.round : 1,
      startedAt: typeof j.startedAt === 'number' ? j.startedAt : null,
      taskId: typeof j.taskId === 'string' ? j.taskId : null,
      title: typeof j.title === 'string' ? j.title : null,
      lastEnded: null,
    }
  } catch {
    return base
  }
}

const write = (s: PomodoroState) => {
  try {
    const { lastEnded: _l, ...rest } = s
    localStorage.setItem(KEY, JSON.stringify(rest))
  } catch {
    /* private mode: the session still works */
  }
}

export const phaseMinutes = (c: PomodoroConfig, p: Phase) =>
  p === 'work' ? c.work : p === 'short' ? c.short : c.long

/** What follows a phase: work → a break (long every `rounds`), a break → work (the next round). */
export const nextPhase = (
  c: PomodoroConfig,
  phase: Phase,
  round: number,
): { phase: Phase; round: number } => {
  if (phase === 'work') return { phase: round % c.rounds === 0 ? 'long' : 'short', round }
  return { phase: 'work', round: phase === 'long' ? 1 : round + 1 }
}

/** Milliseconds left right now. */
export const remainingMs = (s: PomodoroState, now = Date.now()) =>
  s.status === 'running' ? Math.max(0, s.deadline - now) : s.remaining

interface Store extends PomodoroState {
  setConfig: (patch: Partial<PomodoroConfig>) => void
  /** start the current phase (or a fresh work phase when idle), optionally on a task */
  start: (task?: { id: string; title: string } | null) => void
  pause: () => void
  resume: () => void
  /** end the phase now: a work phase is recorded as abandoned unless it was nearly done */
  skip: () => void
  reset: () => void
  /** called by whichever screen notices the deadline passed */
  finish: () => void
  clearEnded: () => void
}

const chime = (phase: Phase) => {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const notes = phase === 'work' ? [523.25, 659.25, 783.99] : [659.25, 523.25]
    notes.forEach((f, i) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'sine'
      o.frequency.value = f
      g.gain.setValueAtTime(0, ctx.currentTime + i * 0.18)
      g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + i * 0.18 + 0.02)
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.5)
      o.connect(g).connect(ctx.destination)
      o.start(ctx.currentTime + i * 0.18)
      o.stop(ctx.currentTime + i * 0.18 + 0.55)
    })
    setTimeout(() => void ctx.close(), 2000)
  } catch {
    /* no audio here */
  }
}

const notify = (title: string, body: string) => {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    void new Notification(title, { body, tag: 'pomodoro', silent: true })
  } catch {
    /* fine */
  }
}

const record = (s: PomodoroState, completed: boolean, minutes: number) => {
  if (s.phase !== 'work' || minutes < 1) return
  void api
    .focusRecord({
      at: new Date().toISOString(),
      minutes: Math.round(minutes * 10) / 10,
      phase: 'work',
      completed,
      task_id: s.taskId ?? undefined,
      title: s.title ?? undefined,
      round: s.round,
    })
    .catch(() => {
      /* offline: the session is not worth queueing */
    })
}

export const usePomodoro = create<Store>((set, get) => ({
  ...read(),
  setConfig: (patch) => {
    const s = get()
    const config = { ...s.config, ...patch }
    const next: PomodoroState = { ...s, config }
    if (s.status === 'idle') next.remaining = phaseMinutes(config, s.phase) * 60_000
    set(next)
    write(next)
  },
  start: (task) => {
    const s = get()
    const ms = s.status === 'paused' ? s.remaining : phaseMinutes(s.config, s.phase) * 60_000
    const next: PomodoroState = {
      ...s,
      status: 'running',
      deadline: Date.now() + ms,
      remaining: ms,
      startedAt: s.status === 'paused' ? s.startedAt : Date.now(),
      taskId: task ? task.id : s.status === 'paused' ? s.taskId : null,
      title: task ? task.title : s.status === 'paused' ? s.title : null,
      lastEnded: null,
    }
    set(next)
    write(next)
  },
  pause: () => {
    const s = get()
    if (s.status !== 'running') return
    const next: PomodoroState = { ...s, status: 'paused', remaining: remainingMs(s) }
    set(next)
    write(next)
  },
  resume: () => get().start(),
  skip: () => {
    const s = get()
    if (s.status === 'idle') return
    const total = phaseMinutes(s.config, s.phase) * 60_000
    const done = total - remainingMs(s)
    record(s, done >= total * 0.9, done / 60_000)
    const nx = nextPhase(s.config, s.phase, s.round)
    const next: PomodoroState = {
      ...s,
      phase: nx.phase,
      round: nx.round,
      status: 'idle',
      deadline: 0,
      remaining: phaseMinutes(s.config, nx.phase) * 60_000,
      startedAt: null,
      lastEnded: null,
    }
    set(next)
    write(next)
  },
  reset: () => {
    const s = get()
    if (s.status === 'running' || s.status === 'paused') {
      const total = phaseMinutes(s.config, s.phase) * 60_000
      record(s, false, (total - remainingMs(s)) / 60_000)
    }
    const next: PomodoroState = {
      ...s,
      phase: 'work',
      round: 1,
      status: 'idle',
      deadline: 0,
      remaining: s.config.work * 60_000,
      startedAt: null,
      taskId: null,
      title: null,
      lastEnded: null,
    }
    set(next)
    write(next)
  },
  finish: () => {
    const s = get()
    if (s.status !== 'running' || remainingMs(s) > 0) return
    record(s, true, phaseMinutes(s.config, s.phase))
    if (s.config.sound) chime(s.phase)
    if (s.config.notify)
      notify(
        s.phase === 'work' ? 'Focus done' : 'Break over',
        s.phase === 'work' ? `Round ${s.round} finished. Take a break.` : 'Back to it.',
      )
    const nx = nextPhase(s.config, s.phase, s.round)
    const ms = phaseMinutes(s.config, nx.phase) * 60_000
    const next: PomodoroState = {
      ...s,
      phase: nx.phase,
      round: nx.round,
      status: s.config.autoNext ? 'running' : 'idle',
      deadline: s.config.autoNext ? Date.now() + ms : 0,
      remaining: ms,
      startedAt: s.config.autoNext ? Date.now() : null,
      lastEnded: { phase: s.phase, at: Date.now() },
    }
    set(next)
    write(next)
  },
  clearEnded: () => set({ lastEnded: null }),
}))

/** Ask for notification permission once, from a user gesture. */
export const askNotify = () => {
  try {
    if ('Notification' in window && Notification.permission === 'default')
      void Notification.requestPermission()
  } catch {
    /* fine */
  }
}
